import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import ApplicationsTab from './components/ApplicationsTab';
import AppointmentsTab from './components/AppointmentsTab';
import RemindersTab from './components/RemindersTab';
import RescheduleTab from './components/RescheduleTab';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'applications' | 'appointments' | 'reminders' | 'reschedule'>('applications');
  const [stats, setStats] = useState({
    totalApplications: 0,
    qualified: 0,
    notQualified: 0,
    totalAppointments: 0,
    upcomingNext7Days: 0,
  });

  useEffect(() => {
    const isAuth = sessionStorage.getItem('mmp_admin_auth');
    if (!isAuth) {
      navigate('/admin');
    }
  }, [navigate]);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    const now = new Date();
    const in7Days = new Date();
    in7Days.setDate(now.getDate() + 7);

    const [applicationsRes, appointmentsRes, upcomingRes] = await Promise.all([
      supabase.from('applications').select('is_qualified'),
      supabase.from('appointments').select('id'),
      supabase
        .from('appointments')
        .select('id')
        .gte('appointment_date', now.toISOString())
        .lte('appointment_date', in7Days.toISOString()),
    ]);

    const applications = applicationsRes.data || [];
    const qualified = applications.filter(app => app.is_qualified).length;
    const notQualified = applications.filter(app => !app.is_qualified).length;

    setStats({
      totalApplications: applications.length,
      qualified,
      notQualified,
      totalAppointments: appointmentsRes.data?.length || 0,
      upcomingNext7Days: upcomingRes.data?.length || 0,
    });
  };

  const handleLogout = () => {
    sessionStorage.removeItem('mmp_admin_auth');
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Navbar */}
      <nav className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <img
              src="https://public.readdy.ai/ai/img_res/28ab250a6f5cee368ac07c4ec9972d11.jpg"
              alt="Monthly Millionaire Project"
              className="h-10 object-contain"
            />
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-stone-800 text-white rounded-lg hover:bg-stone-900 transition-colors text-sm whitespace-nowrap cursor-pointer"
            >
              Logout
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Bar */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-lg border border-stone-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-600 mb-1">Total Applications</p>
                <p className="text-3xl font-bold text-stone-900">{stats.totalApplications}</p>
              </div>
              <div className="w-12 h-12 bg-stone-100 rounded-lg flex items-center justify-center">
                <i className="ri-file-list-3-line text-2xl text-stone-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-stone-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-600 mb-1">Qualified</p>
                <p className="text-3xl font-bold text-emerald-600">{stats.qualified}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-2xl text-emerald-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-stone-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-600 mb-1">Not Qualified</p>
                <p className="text-3xl font-bold text-red-600">{stats.notQualified}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <i className="ri-close-circle-line text-2xl text-red-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-stone-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-600 mb-1">Total Appointments</p>
                <p className="text-3xl font-bold text-stone-900">{stats.totalAppointments}</p>
              </div>
              <div className="w-12 h-12 bg-stone-100 rounded-lg flex items-center justify-center">
                <i className="ri-calendar-check-line text-2xl text-stone-600"></i>
              </div>
            </div>
          </div>

          {/* Upcoming Next 7 Days Card */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg border border-amber-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-700 mb-1 font-medium">Next 7 Days</p>
                <p className="text-3xl font-bold text-amber-600">{stats.upcomingNext7Days}</p>
                <p className="text-xs text-amber-600 mt-1">upcoming</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <i className="ri-calendar-todo-line text-2xl text-amber-600"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
          <div className="border-b border-stone-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('applications')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === 'applications'
                    ? 'bg-emerald-50 text-emerald-700 border-b-2 border-emerald-600'
                    : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
                }`}
              >
                Applications
              </button>
              <button
                onClick={() => setActiveTab('appointments')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === 'appointments'
                    ? 'bg-emerald-50 text-emerald-700 border-b-2 border-emerald-600'
                    : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
                }`}
              >
                Appointments
              </button>
              <button
                onClick={() => setActiveTab('reminders')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === 'reminders'
                    ? 'bg-emerald-50 text-emerald-700 border-b-2 border-emerald-600'
                    : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
                }`}
              >
                Reminders
              </button>
              <button
                onClick={() => setActiveTab('reschedule')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === 'reschedule'
                    ? 'bg-amber-50 text-amber-700 border-b-2 border-amber-500'
                    : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
                }`}
              >
                <span className="flex items-center justify-center gap-1.5">
                  <i className="ri-calendar-line text-sm"></i>
                  Reschedule
                </span>
              </button>
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'applications' && <ApplicationsTab />}
            {activeTab === 'appointments' && <AppointmentsTab />}
            {activeTab === 'reminders' && <RemindersTab />}
            {activeTab === 'reschedule' && <RescheduleTab />}
          </div>
        </div>
      </div>
    </div>
  );
}
